import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import {
  ArrowLeft, Link2, Mail, Download, Loader2, MessagesSquare, Users2, Clock3, Gauge, QrCode,
} from "lucide-react";
import { InviteDialog } from "../../components/discovery/InviteDialog";
import { QrDialog } from "../../components/discovery/QrDialog";

const AREA_LABEL = { direzione: "Direzione", backoffice: "Backoffice", crediti: "Crediti", marketing: "Marketing" };
const AREA_COLORS = { direzione: "#3B82F6", backoffice: "#10B981", crediti: "#F59E0B", marketing: "#8B5CF6" };
const BAND_COLORS = { ALTO: "#EF4444", MEDIO: "#F59E0B", BASSO: "#10B981" };
const STATUS_BADGE = {
  open: { label: "Aperto", cls: "bg-success-subtle text-success-strong" },
  paused: { label: "In pausa", cls: "bg-warning-subtle text-warning-strong" },
  closed: { label: "Chiuso", cls: "bg-subtle text-text-muted" },
};

const fmtDT = (s) => (s ? new Date(s).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtDur = (s) => (s == null ? "—" : s >= 60 ? `${Math.round(s / 60)} min` : `${s}s`);

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const el = document.createElement("a");
  el.href = url;
  el.download = filename;
  el.click();
  URL.revokeObjectURL(url);
};

export default function SuperAdminDiscoveryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [failed, setFailed] = useState(false);
  const [tab, setTab] = useState("panoramica");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/discovery/assessments/${id}/stats`)
      .then(({ data }) => setStats(data))
      .catch(() => setFailed(true));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  if (failed) {
    return (
      <div className="py-16 text-center text-sm text-text-muted" data-testid="discovery-detail-error">
        Assessment non trovato.
      </div>
    );
  }
  if (!stats) {
    return <div className="py-16 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={20} /></div>;
  }

  const a = stats.assessment;
  const b = STATUS_BADGE[a.status] || STATUS_BADGE.open;

  const copyLink = () => {
    const url = `${window.location.origin}/d/${a.slug_token}`;
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Link copiato negli appunti"))
      .catch(() => toast.info(url));
  };
  const exportContactsCsv = async () => {
    try {
      const { data } = await api.get(`/discovery/assessments/${id}/contacts/export`, { responseType: "blob" });
      downloadBlob(data, `contatti-${a.title.slice(0, 30).replace(/\s+/g, "_")}.csv`);
    } catch {
      toast.error("Export non riuscito");
    }
  };
  const exportFullCsv = async () => {
    try {
      const { data } = await api.get(`/discovery/assessments/${id}/export`, {
        params: { format: "csv" }, responseType: "blob",
      });
      downloadBlob(data, `discovery-${a.title.slice(0, 30).replace(/\s+/g, "_")}.csv`);
    } catch {
      toast.error("Export non riuscito");
    }
  };

  const TABS = [
    { id: "panoramica", label: "Panoramica" },
    { id: "contatti", label: `Contatti (${stats.kpi.n_contacts})` },
    { id: "risposte", label: `Risposte (${stats.kpi.n_responses})` },
    { id: "inviti", label: `Inviti (${stats.kpi.n_invites ?? 0})` },
  ];

  return (
    <div className="space-y-6" data-testid="discovery-detail-page">
      <div>
        <button type="button" onClick={() => navigate("/superadmin/discovery")}
                className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary mb-3"
                data-testid="discovery-detail-back-btn">
          <ArrowLeft size={15} /> Tutti gli assessment
        </button>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-text-primary flex items-center gap-3">
              {a.title}
              <span className={`text-xs px-2 py-0.5 rounded-md font-normal ${b.cls}`} data-testid="discovery-detail-status">{b.label}</span>
            </h1>
            <p className="text-sm text-text-secondary mt-1">
              {a.sector || "Nessun settore"} · creato il {fmtDT(a.created_at)} · contatto: {a.settings?.contact} · reazioni: {a.settings?.reactions ? "on" : "off"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={copyLink} className="wl-btn wl-btn--ghost" data-testid="discovery-detail-copy">
              <Link2 size={14} className="mr-1.5" /> Copia link
            </button>
            <button type="button" onClick={() => setQrOpen(true)} className="wl-btn wl-btn--ghost" data-testid="discovery-detail-qr">
              <QrCode size={14} className="mr-1.5" /> QR code
            </button>
            <button type="button" onClick={() => setInviteOpen(true)} className="wl-btn wl-btn--primary"
                    data-testid="discovery-detail-invite">
              <Mail size={14} className="mr-1.5" /> Invita via email
            </button>
          </div>
        </div>
      </div>

      <div className="flex gap-1 border-b border-border" data-testid="discovery-detail-tabs">
        {TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  data-testid={`discovery-tab-${t.id}`}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === t.id
                      ? "border-accent text-accent-strong"
                      : "border-transparent text-text-secondary hover:text-text-primary"
                  }`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === "panoramica" && <Overview stats={stats} onExport={exportFullCsv} />}
      {tab === "contatti" && <ContactsTab id={id} onExport={exportContactsCsv} />}
      {tab === "risposte" && <ResponsesTab id={id} />}
      {tab === "inviti" && <InvitesTab id={id} />}

      <InviteDialog assessment={inviteOpen ? a : null} onClose={() => setInviteOpen(false)} onSent={load} />
      {qrOpen && <QrDialog assessment={a} onClose={() => setQrOpen(false)} />}
    </div>
  );
}

const KpiCard = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-surface border border-border rounded-xl p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-text-muted uppercase tracking-wider mb-2">
      <Icon size={14} /> {label}
    </div>
    <div className="text-2xl font-semibold text-text-primary">{value}</div>
    {sub && <div className="text-xs text-text-muted mt-1">{sub}</div>}
  </div>
);

const Overview = ({ stats, onExport }) => {
  const { kpi, index, by_area, questions } = stats;
  const pieData = by_area.filter((x) => x.n > 0).map((x) => ({ name: x.label, value: x.n, area: x.area }));
  const totBands = index.bands.ALTO + index.bands.MEDIO + index.bands.BASSO;

  if (kpi.n_responses === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-12 text-center text-sm text-text-muted" data-testid="discovery-stats-empty">
        Nessuna risposta ancora: condividi il link o invia un invito via email.
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="discovery-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard icon={MessagesSquare} label="Risposte" value={kpi.n_responses} testId="kpi-responses" />
        <KpiCard icon={Users2} label="Contatti" value={kpi.n_contacts}
                 sub={`${Math.round(kpi.contact_rate * 100)}% delle risposte`} testId="kpi-contacts" />
        <KpiCard icon={Clock3} label="Durata media" value={fmtDur(kpi.avg_duration_s)} testId="kpi-duration" />
        <KpiCard icon={Gauge} label="Indice medio" value={index.avg ?? "—"}
                 sub="manualità 0–100" testId="kpi-index" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-surface border border-border rounded-xl p-6" data-testid="discovery-index-card">
          <h3 className="text-sm font-semibold text-text-primary mb-4">Indice di manualità</h3>
          <div className="flex h-3 rounded-full overflow-hidden bg-subtle mb-3">
            {["ALTO", "MEDIO", "BASSO"].map((band) =>
              index.bands[band] > 0 ? (
                <div key={band} style={{ width: `${(index.bands[band] / totBands) * 100}%`, background: BAND_COLORS[band] }} />
              ) : null
            )}
          </div>
          <div className="space-y-2">
            {["ALTO", "MEDIO", "BASSO"].map((band) => (
              <div key={band} className="flex items-center justify-between text-sm" data-testid={`band-row-${band}`}>
                <span className="flex items-center gap-2 text-text-secondary">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ background: BAND_COLORS[band] }} />
                  {band}
                </span>
                <span className="text-text-primary font-medium">
                  {index.bands[band]} <span className="text-text-muted font-normal">({totBands ? Math.round((index.bands[band] / totBands) * 100) : 0}%)</span>
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-surface border border-border rounded-xl p-6" data-testid="discovery-area-card">
          <h3 className="text-sm font-semibold text-text-primary mb-2">Risposte per area</h3>
          <div className="flex items-center gap-4">
            <div className="w-36 h-36 shrink-0">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={38} outerRadius={62} strokeWidth={2}>
                    {pieData.map((e) => <Cell key={e.area} fill={AREA_COLORS[e.area]} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [`${v} risposte`, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-2">
              {by_area.map((x) => (
                <div key={x.area} className="flex items-center justify-between text-sm" data-testid={`area-row-${x.area}`}>
                  <span className="flex items-center gap-2 text-text-secondary">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: AREA_COLORS[x.area] }} />
                    {x.label}
                  </span>
                  <span className="text-text-primary font-medium">
                    {x.n}
                    {x.avg_index != null && <span className="text-text-muted font-normal text-xs ml-1.5">indice {x.avg_index}</span>}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl p-6" data-testid="discovery-questions-card">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-sm font-semibold text-text-primary">Distribuzione risposte per domanda</h3>
          <button type="button" onClick={onExport} className="wl-btn wl-btn--ghost wl-btn--sm" data-testid="discovery-detail-export-full">
            <Download size={13} className="mr-1.5" /> CSV completo
          </button>
        </div>
        <div className="space-y-7">
          {questions.filter((q) => q.n_answers > 0).map((q) => <QuestionBars key={q.qid} q={q} />)}
        </div>
      </div>
    </div>
  );
};

const QuestionBars = ({ q }) => {
  const max = Math.max(1, ...q.options.map((o) => o.count));
  const total = q.type === "multi" ? q.n_answers : q.options.reduce((s, o) => s + o.count, 0);
  return (
    <div data-testid={`qstats-${q.qid}`}>
      <div className="flex items-baseline justify-between gap-3 mb-2.5">
        <div className="text-sm font-medium text-text-primary leading-snug">
          {q.label}
          <span className="text-xs text-text-muted font-normal ml-2">{AREA_LABEL[q.area] || "Comune"} · {q.n_answers} risposte</span>
        </div>
        {q.avg != null && (
          <div className="text-sm font-semibold text-accent-strong shrink-0" data-testid={`qstats-${q.qid}-avg`}>media {q.avg}</div>
        )}
      </div>
      <div className="space-y-1.5">
        {q.options.map((o) => (
          <div key={o.value} className="flex items-center gap-3">
            <div className="w-44 sm:w-56 text-xs text-text-secondary truncate shrink-0" title={o.value}>{o.value}</div>
            <div className="flex-1 h-5 bg-subtle rounded overflow-hidden">
              <div className="h-full rounded bg-accent/80 transition-all" style={{ width: `${(o.count / max) * 100}%` }} />
            </div>
            <div className="w-16 text-xs text-text-primary text-right shrink-0">
              {o.count}
              <span className="text-text-muted ml-1">{total ? Math.round((o.count / total) * 100) : 0}%</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const BandPill = ({ band, score }) => (
  <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-md"
        style={{ background: `${BAND_COLORS[band]}18`, color: BAND_COLORS[band] }}>
    {band} · {score}
  </span>
);

const ContactsTab = ({ id, onExport }) => {
  const [contacts, setContacts] = useState(null);

  useEffect(() => {
    api.get(`/discovery/assessments/${id}/contacts`)
      .then(({ data }) => setContacts(data.contacts))
      .catch(() => toast.error("Impossibile caricare i contatti"));
  }, [id]);

  if (!contacts) {
    return <div className="py-12 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={18} /></div>;
  }
  if (contacts.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-12 text-center text-sm text-text-muted" data-testid="discovery-contacts-empty">
        Nessun contatto raccolto ancora.
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="discovery-contacts-tab">
      <div className="flex justify-end">
        <button type="button" onClick={onExport} className="wl-btn wl-btn--ghost wl-btn--sm" data-testid="discovery-contacts-export">
          <Download size={13} className="mr-1.5" /> Esporta contatti CSV
        </button>
      </div>
      <div className="bg-surface border border-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
            <tr>
              <th className="text-left px-5 py-3 font-medium">Nome</th>
              <th className="text-left px-3 py-3 font-medium">Azienda</th>
              <th className="text-left px-3 py-3 font-medium">Email</th>
              <th className="text-left px-3 py-3 font-medium">Telefono</th>
              <th className="text-left px-3 py-3 font-medium">Area</th>
              <th className="text-left px-3 py-3 font-medium">Indice</th>
              <th className="text-left px-5 py-3 font-medium">Consenso</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.response_id} className="border-t border-border" data-testid={`contact-row-${c.response_id}`}>
                <td className="px-5 py-3 text-text-primary font-medium">{c.name || "—"}</td>
                <td className="px-3 py-3 text-text-secondary">{c.company || "—"}</td>
                <td className="px-3 py-3"><a href={`mailto:${c.email}`} className="text-accent-strong hover:underline">{c.email}</a></td>
                <td className="px-3 py-3 text-text-secondary">{c.phone || "—"}</td>
                <td className="px-3 py-3 text-text-secondary">{AREA_LABEL[c.area] || c.area}</td>
                <td className="px-3 py-3"><BandPill band={c.band} score={c.index} /></td>
                <td className="px-5 py-3 text-text-secondary text-xs">{fmtDT(c.consent_ts)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const ResponsesTab = ({ id }) => {
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    api.get(`/discovery/assessments/${id}/responses`)
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error("Impossibile caricare le risposte"));
  }, [id]);

  if (!data) {
    return <div className="py-12 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={18} /></div>;
  }
  if (data.responses.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-12 text-center text-sm text-text-muted" data-testid="discovery-no-responses">
        Nessuna risposta ancora.
      </div>
    );
  }
  const labelByQid = Object.fromEntries((data.questions || []).map((q) => [q.qid, q.label]));

  if (detail) {
    return (
      <div className="bg-surface border border-border rounded-xl p-6" data-testid="discovery-response-detail">
        <button type="button" onClick={() => setDetail(null)} className="wl-btn wl-btn--ghost wl-btn--sm mb-4" data-testid="discovery-detail-back">
          ← Torna alla lista
        </button>
        <div className="text-sm text-text-secondary mb-4">
          {AREA_LABEL[detail.area]} · {detail.respondent?.role || "ruolo n/d"} · {fmtDT(detail.meta?.submitted_at)}
          {detail.contact?.email && <> · <b className="text-text-primary">{detail.contact.email}</b></>}
        </div>
        <div className="space-y-3">
          {detail.answers.map((x) => (
            <div key={x.qid} className="bg-subtle border border-border rounded-lg px-4 py-3">
              <div className="text-xs text-text-muted mb-1">{labelByQid[x.qid] || x.qid}</div>
              <div className="text-sm text-text-primary">{Array.isArray(x.value) ? x.value.join(", ") : String(x.value)}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface border border-border rounded-xl overflow-x-auto" data-testid="discovery-responses-tab">
      <table className="w-full text-sm min-w-[680px]">
        <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
          <tr>
            <th className="text-left px-5 py-3 font-medium">Data</th>
            <th className="text-left px-3 py-3 font-medium">Area</th>
            <th className="text-left px-3 py-3 font-medium">Ruolo</th>
            <th className="text-left px-3 py-3 font-medium">Contatto</th>
            <th className="text-right px-5 py-3 font-medium">Durata</th>
          </tr>
        </thead>
        <tbody>
          {data.responses.map((r) => (
            <tr key={r.id} className="border-t border-border cursor-pointer hover:bg-subtle" onClick={() => setDetail(r)}
                data-testid={`discovery-response-row-${r.id}`}>
              <td className="px-5 py-2.5 text-text-secondary text-xs">{fmtDT(r.meta?.submitted_at)}</td>
              <td className="px-3 py-2.5">{AREA_LABEL[r.area] || r.area}</td>
              <td className="px-3 py-2.5 text-text-secondary">{r.respondent?.role || "—"}</td>
              <td className="px-3 py-2.5">{r.contact?.email || <span className="text-text-muted">—</span>}</td>
              <td className="px-5 py-2.5 text-right text-text-secondary text-xs">{fmtDur(r.meta?.duration_s)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const InvitesTab = ({ id }) => {
  const [invites, setInvites] = useState(null);

  useEffect(() => {
    api.get(`/discovery/assessments/${id}/invites`)
      .then(({ data }) => setInvites(data.invites))
      .catch(() => toast.error("Impossibile caricare gli inviti"));
  }, [id]);

  if (!invites) {
    return <div className="py-12 text-center text-text-muted"><Loader2 className="animate-spin mx-auto" size={18} /></div>;
  }
  if (invites.length === 0) {
    return (
      <div className="bg-surface border border-border rounded-xl p-12 text-center text-sm text-text-muted" data-testid="discovery-invites-empty">
        Nessun invito inviato ancora. Usa «Invita via email» per contattare i prospect.
      </div>
    );
  }
  return (
    <div className="bg-surface border border-border rounded-xl overflow-x-auto" data-testid="discovery-invites-tab">
      <table className="w-full text-sm min-w-[680px]">
        <thead className="bg-subtle text-xs uppercase tracking-wider text-text-secondary">
          <tr>
            <th className="text-left px-5 py-3 font-medium">Destinatario</th>
            <th className="text-left px-3 py-3 font-medium">Oggetto</th>
            <th className="text-left px-3 py-3 font-medium">Inviato il</th>
            <th className="text-left px-5 py-3 font-medium">Esito</th>
          </tr>
        </thead>
        <tbody>
          {invites.map((i) => (
            <tr key={i.id} className="border-t border-border" data-testid={`invite-row-${i.id}`}>
              <td className="px-5 py-3">
                <div className="text-text-primary font-medium">{i.to}</div>
                {i.name && <div className="text-[11px] text-text-muted">{i.name}</div>}
              </td>
              <td className="px-3 py-3 text-text-secondary text-xs max-w-[280px] truncate" title={i.subject}>{i.subject}</td>
              <td className="px-3 py-3 text-text-secondary text-xs">{fmtDT(i.sent_at)}</td>
              <td className="px-5 py-3">
                {i.responded ? (
                  <span className="text-xs px-2 py-0.5 rounded-md bg-success-subtle text-success-strong" data-testid={`invite-responded-${i.id}`}>
                    Ha risposto
                  </span>
                ) : (
                  <span className="text-xs px-2 py-0.5 rounded-md bg-subtle text-text-muted">In attesa</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
